import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select"

export function SelectRenderer({ element }: RendererProps) {
  const { resolveSymbol, dispatchEvent } = useRenderContext()
  const placeholder = element.placeholder
    ? String(resolveSymbol(element.placeholder) ?? "")
    : undefined

  // Support bind for controlled value — wrap in #{} to resolve as state path
  const boundValue = element.bind
    ? String(resolveSymbol(`#{${element.bind}}`) ?? "")
    : undefined

  const handleValueChange = (value: string) => {
    dispatchEvent(element.id, "change", { value })
  }

  // Support both formats: element.options [{label,value}] and element.children [{type:"option"}]
  const rawOptions = (element as unknown as Record<string, unknown>).options
  // If options is a string expression (e.g. "#{ui.itemGroups}"), resolve it
  const resolvedOptions = typeof rawOptions === "string" ? resolveSymbol(rawOptions) : rawOptions
  const optionsFromProp = Array.isArray(resolvedOptions) ? resolvedOptions as Array<{label?: string; value?: string}> : undefined
  const childOptions = element.children?.filter(c => c.type === "option" || c.text) ?? []

  // Dynamic options from state: optionsData is a state path, valueKey/labelKey define mapping
  const el = element as unknown as Record<string, unknown>
  const dynamicData = el.optionsData ? resolveSymbol(el.optionsData as string) : undefined
  const dynamicOptions = Array.isArray(dynamicData) ? dynamicData as Record<string, unknown>[] : undefined
  const valueKey = String(el.valueKey ?? "value")
  const labelKey = String(el.labelKey ?? "label")

  // Merge: static options first, then dynamic options
  const allOptions: Array<{label: string; value: string}> = []
  if (optionsFromProp) {
    for (const opt of optionsFromProp) {
      const v = String(opt.value ?? "")
      if (!v) continue // Skip empty values — Radix Select requires non-empty value
      allOptions.push({ label: String(resolveSymbol(opt.label ?? "") ?? ""), value: v })
    }
  }
  if (dynamicOptions) {
    for (const item of dynamicOptions) {
      const v = String(item[valueKey] ?? "")
      const l = String(item[labelKey] ?? v)
      if (v) allOptions.push({ label: l, value: v })
    }
  }

  return (
    <Select value={boundValue} onValueChange={handleValueChange}>
      <SelectTrigger id={element.id} className={element.className}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {allOptions.length > 0
          ? allOptions.map((opt, i) => (
              <SelectItem key={opt.value || i} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))
          : childOptions.map((opt, i) => (
              <SelectItem key={opt.id || i} value={opt.variant || String(resolveSymbol(opt.text ?? "") ?? "")}>
                {String(resolveSymbol(opt.text ?? "") ?? "")}
              </SelectItem>
            ))
        }
      </SelectContent>
    </Select>
  )
}
